import {
    ChannelEventList,
    EventListContainer,
    ScheduledSession,
} from "@kaltura/mediaspace-shared-event-platform-event-list";
import { ButtonClickAnalyticsType, CategorySectionProps, FilterByTags } from "@kaltura/mediaspace-shared-types";
import { useEffect, useMemo, useState } from "react";
import {
    appendFiltersToQueryString,
    FilterBar,
    filterSessions,
    FiltersValues,
    getFiltersValuesFromQueryString,
    removeAllQueryParams,
} from "@kaltura/mediaspace-shared-search-with-filter-tags";
import omitBy from "lodash/omitBy";
import { NoResults } from "@kaltura/mediaspace-shared-search";
import { useThrottledValue } from "@kaltura/mediaspace-shared-hooks";
import { sendButtonClickAnalytics, translate } from "@kaltura/mediaspace-shared-utils";
import { styled } from "@mui/material";

export interface SessionsProps extends CategorySectionProps<ScheduledSession[]> {
    hideFilters?: boolean;
    filters?: FilterByTags[];
    showSearchInput: boolean;
}

const StyledFilterBarContainer = styled("div")(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

/**
 * Channel page sessions section - entries which contain scheduling data
 */
export function Sessions(props: SessionsProps) {
    const { content: scheduledSessions, hideFilters, categoryId, filters = [], showSearchInput } = props;

    const [searchTermValue, setSearchTermValue] = useState<string>("");
    const throttledSearchTermValue = useThrottledValue(searchTermValue, 1000);

    const [tagFilterValues, setTagFilterValues] = useState<FiltersValues>({});

    const filteredSessions = useMemo(
        () => filterSessions(scheduledSessions, searchTermValue, tagFilterValues),
        [scheduledSessions, searchTermValue, tagFilterValues]
    );

    useEffect(() => {
        if (throttledSearchTermValue) {
            sendButtonClickAnalytics("Search", "Channel - Sessions tab", ButtonClickAnalyticsType.SEARCH);
        }
    }, [throttledSearchTermValue]);

    useEffect(() => {
        const filterValuesFromQueryString = getFiltersValuesFromQueryString(filters);
        Object
            .entries(filterValuesFromQueryString)
            .forEach(([key, value]) => {
                handleTagFilterChange(key, value, false);
            });
    }, [filters]);

    const handleSearchTermChange = (value: string) => {
        // set search term input value
        setSearchTermValue(value);
    };

    const handleSearchTermReset = () => {
        // clear search term input value
        setSearchTermValue("");
    };

    const handleTagFilterChange = (param: string, values: string[], appendToQueryString = true) => {
        // new filters values, without empty filters
        setTagFilterValues((filterValues) => omitBy({ ...filterValues, [param]: values }, (elem) => elem.length === 0));
        if (appendToQueryString) {
            appendFiltersToQueryString(param, values);
        }

        sendButtonClickAnalytics("Filter by tag", "Channel - Sessions tab", ButtonClickAnalyticsType.SEARCH);
    };

    const handleClearBubbles = () => {
        // clear all filters
        setTagFilterValues({});
        removeAllQueryParams(filters);
    };

    return (
        <div className={"kms-ds-channel-page-section-sessions"}>
            {!hideFilters && (
                <StyledFilterBarContainer>
                    <FilterBar
                        onInputChange={handleSearchTermChange}
                        onInputReset={handleSearchTermReset}
                        onClearBubbles={handleClearBubbles}
                        onFilterChange={handleTagFilterChange}
                        filters={filters}
                        values={tagFilterValues}
                        withSearchInput={showSearchInput}
                        searchInputPlaceholder={translate("Search in Sessions")}
                    />
                </StyledFilterBarContainer>
            )}
            {filteredSessions.length > 0 ? (
                <EventListContainer
                    sessions={filteredSessions}
                    eventListComponent={ChannelEventList}
                    hideFilterButton={hideFilters}
                    categoryId={categoryId}
                />
            ) : (
                <NoResults screenMode={"nomatches"} />
            )}
        </div>
    );
}

export default Sessions;
